import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { Tenancy_Rich_Asset_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import { Select, SelectOption } from '@pypestream/design-system';

import { SelectPlaceholder } from './select-placeholder';
import { NodeColumns, TableRow, tableStore } from '../../stores/table-store';
import { RichAssetTypeConfig } from '../../stores/rich-assets';
import { getAssetLabelByType, getAssetTypeList } from './utils';

interface NodeRichAssetSingleSelectProps {
  rowId: string;
  testId?: string;
}

export const NodeRichAssetSingleSelect: React.FC<NodeRichAssetSingleSelectProps> =
  observer(({ rowId, testId }) => {
    const [isSelectRendered, setIsSelectRendered] = useState(false);
    const selectRef = useRef<React.ComponentRef<typeof Select>>(null);

    const isAssetContentCellEmpty = !tableStore.getValue(
      rowId,
      NodeColumns.richAssetContent
    );

    const assetTypesWithPrefilledContent = [
      Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Buttons,
      Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Audio,
      Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Button,
    ];

    const initialValue = tableStore.getValue(
      rowId,
      NodeColumns.richAssetType
    ) as TableRow[NodeColumns.richAssetType];

    const displayValue = getAssetLabelByType(initialValue);

    useEffect(() => {
      if (isSelectRendered && selectRef.current) {
        // Use a longer delay to ensure the component is fully rendered
        setTimeout(() => {
          if (
            selectRef.current &&
            typeof selectRef.current.show === 'function'
          ) {
            selectRef.current.show();
            if (selectRef.current.focus) {
              selectRef.current.focus();
            }
          }
        }, 50);
      }
    }, [isSelectRendered]);

    if (tableStore.isEditing()) {
      if (!isSelectRendered) {
        return (
          <SelectPlaceholder
            testId={`${testId}-placeholder`}
            onOpen={() => setIsSelectRendered(true)}
            ariaLabel="Open rich asset type selection"
          >
            <span>{displayValue}</span>
          </SelectPlaceholder>
        );
      }

      return (
        <Select
          ref={selectRef}
          value={initialValue}
          test-id={testId}
          size="small"
          variant="outlined"
          autoSizePadding={36}
          onclose={(_ev) => {
            setTimeout(() => {
              tableStore.isCellBeingEdited = false;
            }, 100);

            setTimeout(() => {
              if (selectRef.current?.open) return;

              if (isSelectRendered) {
                setIsSelectRendered(false); // Reset to fake select after closing
              }
            }, 2000);
          }}
          onmouseleave={() => {
            if (selectRef.current?.open) return;

            setTimeout(() => {
              if (isSelectRendered) {
                setIsSelectRendered(false); // Reset to fake select after closing
              }
            }, 500);
          }}
          onChange={(e) => {
            const newValue = e.target.value;

            if (
              Object.values(Tenancy_Rich_Asset_Type_Enum_Ps_Enum).includes(
                e.target.value as Tenancy_Rich_Asset_Type_Enum_Ps_Enum
              )
            ) {
              // Dropdowns should save immediately. Wrap the asset-type change
              // (which may also seed default content) in one undo/redo command
              // (PSTUDIO-2154).
              tableStore.recordRowEdit(rowId, 'Change asset type', () => {
                tableStore.updateRow({
                  rowId,
                  updates: {
                    [NodeColumns.richAssetType]:
                      newValue as Tenancy_Rich_Asset_Type_Enum_Ps_Enum,
                  },
                  skipValidationRules: false,
                  saveImmediately: true,
                });

                tableStore.clearCellValidationError(
                  rowId,
                  NodeColumns.richAssetType
                );

                if (
                  // @TODO: remove these if else statements when we have all the asset types
                  assetTypesWithPrefilledContent.includes(
                    newValue as Tenancy_Rich_Asset_Type_Enum_Ps_Enum
                  ) &&
                  isAssetContentCellEmpty
                ) {
                  const defaultContent =
                    tableStore.richAssetsConfig[
                      newValue as keyof RichAssetTypeConfig
                    ];
                  const contentValue =
                    typeof defaultContent === 'string'
                      ? defaultContent
                      : JSON.stringify(defaultContent, null, 2);

                  // Dropdowns should save immediately
                  tableStore.updateRow({
                    rowId,
                    updates: {
                      [NodeColumns.richAssetContent]: contentValue,
                    } as object,
                    skipValidationRules: false,
                    saveImmediately: true,
                  });

                  tableStore.clearCellValidationError(
                    rowId,
                    NodeColumns.richAssetContent
                  );
                }
              });
              // } else if (newValue === 'asset-type-none' || newValue === 'none') {
              //   tableStore.updateRow(rowId, {
              //     [NodeColumns.richAssetType]: undefined,
              //   });
              //   tableStore.updateRow(rowId, {
              //     [NodeColumns.richAssetContent]: undefined,
              //   });
            } else {
              console.warn(
                NodeColumns.richAssetType,
                e.target.value,
                'not found in list of known types'
              );
            }

            const closestCell = e.target.closest('td');
            if (closestCell) {
              closestCell.focus();
              setTimeout(() => {
                tableStore.isCellBeingEdited = false;
              }, 100);
            }
          }}
        >
          {getAssetTypeList().map(({ type, label }) => (
            <SelectOption key={type} value={type}>
              {label}
            </SelectOption>
          ))}
        </Select>
      );
    }

    return <>{tableStore.getValue(rowId, NodeColumns.richAssetType)}</>;
  });
